import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr, useFetch, fmtTime } from "@/lib/api";
import { PageHeader, Loading, ErrorBox, Empty, RiskBadge, Pill } from "@/components/common";
import { useAuth, can } from "@/context/AuthContext";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function Approvals() {
  const { orgId, role, user } = useAuth();
  const [status, setStatus] = useState("pending");
  const { data, loading, error, reload } = useFetch(`/approvals?status=${status}`, [orgId]);
  const decide = async (id, decision) => {
    try {
      await api.post(`/approvals/${id}/decide`, { decision, reason: decision === "approve" ? "Approved in portal" : "Rejected in portal" });
      toast.success(decision === "approve" ? "Approved — signed task queued for the device" : "Rejected — incident escalated");
      reload();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div className="fade-up">
      <PageHeader title="Approvals" crumbs={["Portal", "Approvals"]}>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-gray-300 px-3 py-2 text-sm" data-testid="approvals-status-filter">
          <option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option><option value="expired">Expired</option><option value="">All</option>
        </select>
      </PageHeader>
      <ErrorBox error={error} />
      {loading ? <Loading /> : data.length === 0 ? <Empty title="Nothing awaiting approval" text="Moderate (L3) and high-risk (L4) actions, and L2 actions without auto-approval, appear here." testid="approvals-empty" /> : (
        <div className="space-y-3">
          {data.map((a) => (
            <div key={a.id} className={`card-flat p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 ${a.risk_level >= 4 ? "border-l-4 border-l-red-600" : ""}`} data-testid={`approval-card-${a.id}`}>
              <div>
                <div className="flex items-center gap-2"><RiskBadge level={a.risk_level} /><span className="font-mono font-semibold">{a.playbook_id}</span><Pill tone={a.status === "pending" ? "waiting" : a.status === "approved" ? "healthy" : "unknown"}>{a.status}</Pill></div>
                <div className="text-sm text-gray-600 mt-1">Device <b>{a.hostname}</b> · params <span className="font-mono">{JSON.stringify(a.params)}</span></div>
                <div className="text-xs text-gray-400 mt-1">Requested by {a.requested_by} · {fmtTime(a.created_at)} · expires {fmtTime(a.expires_at)}{a.decided_by ? ` · decided by ${a.decided_by}` : ""}</div>
                {a.incident_id && <Link to={`/portal/incidents/${a.incident_id}`} className="text-xs text-red-600 underline">View incident</Link>}
              </div>
              {a.status === "pending" && can.operate(role) && a.risk_level >= 4 && (!can.manage(role) || a.requested_by === user.email) && (
                <div className="text-xs text-gray-500 max-w-[220px]" data-testid={`approval-blocked-${a.id}`}>{a.requested_by === user.email ? "You requested this L4 action — another owner/admin must approve it." : "L4 actions require an owner or administrator."}</div>
              )}
              {a.status === "pending" && can.operate(role) && !(a.risk_level >= 4 && (!can.manage(role) || a.requested_by === user.email)) && (
                <div className="flex gap-2">
                  <button onClick={() => decide(a.id, "reject")} className="px-4 py-2 border border-gray-300 text-sm hover:border-gray-900" data-testid={`approval-reject-${a.id}`}>Reject</button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><button className="px-4 py-2 bg-red-600 text-white text-sm hover:bg-red-700" data-testid={`approval-approve-${a.id}`}>Approve</button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Approve {a.playbook_id} on {a.hostname}?</AlertDialogTitle>
                        <AlertDialogDescription>A signed, time-limited task will be sent to this device only. Risk level {a.risk_level}. This decision is recorded in the audit log.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-red-600" onClick={() => decide(a.id, "approve")} data-testid={`approval-confirm-${a.id}`}>Approve & dispatch</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
